import { GridColumnHeader } from '../components/Grid/types';
import type { Attribute, DataSource } from '../types/entities.types';
import { getSearchableFields } from '../utils/searchable.utils';

const gridHeaders: GridColumnHeader[] = [
  {
    name: 'businessMetadata.asset_name',
    label: 'Semantic View',
    width: '25%',
    filter: {
      type: 'text',
      remoteSearchFields: ['asset_name'],
    },
    sortable: true,
    remoteSortField: 'asset_name',
  },
  {
    name: 'sourceSpecificMetadata.SemanticView.database_name',
    label: 'Database',
    width: '25%',
    filter: {
      type: 'aggregation',
      aggregationName: 'database_aggregation',
      remoteSearchFields: ['database_name'],
      filterSelectProps: {
        isClearable: true,
      },
    },
    sortable: true,
    remoteSortField: 'database_name',
  },
  {
    name: 'sourceSpecificMetadata.SemanticView.schema_name',
    label: 'Schema',
    width: '25%',
    filter: {
      type: 'aggregation',
      aggregationName: 'schema_name_aggregation',
      remoteSearchFields: ['schema_name'],
      filterSelectProps: {
        isClearable: true,
      },
    },
    sortable: true,
    remoteSortField: 'schema_name',
  },
  {
    name: 'businessMetadata.descriptiveMetadata.description',
    label: 'Description',
    width: '25%',
  },
];

const attributesMap: Attribute[] = [
  {
    name: 'businessMetadata.descriptiveMetadata.description',
    label: 'Description',
    showInColumn: 0,
    description: 'Description',
  },
  {
    name: 'sourceSpecificMetadata.SemanticView.database_name',
    label: 'Database',
    showInColumn: 0,
    description: 'Database name',
    isSearchable: true,
  },
  {
    name: 'sourceSpecificMetadata.SemanticView.schema_name',
    label: 'Schema',
    showInColumn: 0,
    description: 'Schema name',
    isSearchable: true,
  },
  {
    name: 'sourceSpecificMetadata.SemanticView.fully_qualified_name',
    label: 'Fully Qualified Name',
    showInColumn: 0,
    description: 'Fully qualified name of the semantic view',
  },
  {
    name: 'technicalMetadata.usageMetadata.last_altered',
    label: 'Last Updated',
    showInColumn: 0,
    description: 'Last updated date',
    type: 'date',
  },
  {
    name: 'sourceSpecificMetadata.SemanticView.statistics.ai_verified_query_count',
    label: 'AI Verified Query Count',
    showInColumn: 1,
    description: 'Number of AI verified queries',
  },
  {
    name: 'sourceSpecificMetadata.SemanticView.statistics.describe_object_count',
    label: 'Describe Object Count',
    showInColumn: 1,
    description: 'Number of describe objects',
  },
  {
    name: 'sourceSpecificMetadata.SemanticView.statistics.dimension_count',
    label: 'Dimension Count',
    showInColumn: 1,
    description: 'Number of dimensions',
  },
  {
    name: 'sourceSpecificMetadata.SemanticView.statistics.fact_count',
    label: 'Fact Count',
    showInColumn: 1,
    description: 'Number of facts',
  },
  {
    name: 'sourceSpecificMetadata.SemanticView.statistics.filter_count',
    label: 'Filter Count',
    showInColumn: 1,
    description: 'Number of filters',
  },
  {
    name: 'sourceSpecificMetadata.SemanticView.statistics.instruction_count',
    label: 'Instruction Count',
    showInColumn: 1,
    description: 'Number of AI instructions',
  },
  {
    name: 'sourceSpecificMetadata.SemanticView.statistics.metric_count',
    label: 'Metric Count',
    showInColumn: 1,
    description: 'Number of metrics',
  },
  {
    name: 'sourceSpecificMetadata.SemanticView.statistics.relationship_count',
    label: 'Relationship Count',
    showInColumn: 1,
    description: 'Number of relationships',
  },
  {
    name: 'sourceSpecificMetadata.SemanticView.statistics.table_count',
    label: 'Table Count',
    showInColumn: 1,
    description: 'Number of tables in the semantic view',
  },
  {
    name: 'sourceSpecificMetadata.SemanticView.statistics.time_dimension_count',
    label: 'Time Dimension Count',
    showInColumn: 1,
    description: 'Number of time dimensions',
  },
  {
    name: 'sourceSpecificMetadata.SemanticView.tables',
    label: '', // Left blank for custom AttributeValueElement implementation
    type: 'custom',
    showInColumn: 2,
    description: 'List of tables for this Semantic View',
  },
  {
    name: 'sourceSpecificMetadata.SemanticView.dimensions',
    label: '', // Left blank for custom AttributeValueElement implementation
    type: 'custom',
    showInColumn: 3,
    description: 'List of dimensions for this Semantic View',
  },
  {
    name: 'sourceSpecificMetadata.SemanticView.facts',
    label: '', // Left blank for custom AttributeValueElement implementation
    type: 'custom',
    showInColumn: 4,
    description: 'List of facts for this Semantic View',
  },
  {
    name: 'sourceSpecificMetadata.SemanticView.metrics',
    label: '', // Left blank for custom AttributeValueElement implementation
    type: 'custom',
    showInColumn: 5,
    description: 'List of metrics for this Semantic View',
  },
  {
    name: 'sourceSpecificMetadata.SemanticView.filters',
    label: '', // Left blank for custom AttributeValueElement implementation
    type: 'custom',
    showInColumn: 6,
    description: 'List of filters for this Semantic View',
  },
  {
    name: 'sourceSpecificMetadata.SemanticView.relationships',
    label: '', // Left blank for custom AttributeValueElement implementation
    type: 'custom',
    showInColumn: 7,
    description: 'List of relationships for this Semantic View',
  },
  {
    name: 'sourceSpecificMetadata.SemanticView.instructions',
    label: '', // Left blank for custom AttributeValueElement implementation
    type: 'custom',
    showInColumn: 8,
    description: 'List of instructions for this Semantic View',
  },
  {
    name: 'sourceSpecificMetadata.SemanticView.verified_queries',
    label: '', // Left blank for custom AttributeValueElement implementation
    type: 'custom',
    showInColumn: 9,
    description: 'List of verified queries for this Semantic View',
  },
];

const customQueryForListPage = `
  SemanticView {
    database_name
    schema_name
  }
`;

const customQueryForDetailsPage = `
  SemanticView {
        database_name
        fully_qualified_name
        schema_name
        dimensions {
          name
          sample_values
          synonyms
          table
          unique
        }
        facts {
          name
          table
        }
        filters {
          description
          expr
          name
          synonyms
          table
        }
        instructions {
          name
          type
          content
        }
        metrics {
          data_type
          name
          table
        }
        relationships {
          description
          join_type
          name
          relationship_type
        }
        statistics {
          ai_verified_query_count
          describe_object_count
          dimension_count
          fact_count
          filter_count
          instruction_count
          metric_count
          relationship_count
          table_count
          time_dimension_count
        }
        tables {
          comment
          database
          fully_qualified_name
          name
          primary_key
          schema
          dimensions {
            name
            sample_values
            synonyms
            table
            unique
          }
          facts {
            name
            table
          }
          filters {
            description
            expr
            name
            synonyms
            table
          }
          metrics {
            data_type
            name
            table
          }
          time_dimensions {
            name
            unique
          }
        }
        verified_queries {
          name
          question
          sql
          use_as_onboarding_question
          verified_at
          verified_by
        }
      }
`;

const searchableFields = getSearchableFields(attributesMap);

export const SemanticViewConfig: DataSource = {
  id: '16',
  name: 'SemanticView',
  showColumns: false,
  showPartitionColumns: false,
  showLineage: false,
  showLineageTable: false,
  showLineageQuery: false,
  showLineageCloned: false,
  showLineageCW: false,
  showLineageColumn: false,
  showLineagePipeline: false,
  showLineageDashboard: false,
  gridHeaders,
  attributesMap,
  customQueryForListPage,
  customQueryForDetailsPage,
  searchableFields,
  glossary: false,
};
